import { getSignupProductByPlan, isDistrictAdminProductKey, type DistrictAdminProductKey, type SignupProductKey } from '../../../config/SignupProductConfig';
import { getProgramBySignupPlan } from '../../../services/ProgramService';
import type { SignupErrorCode } from '../../../components/signup/SignupError/SignupError';
import type { SignupDomainErrorCode } from '../../../components/signup/SignupDomainError/SignupDomainError';
import type { CanvasSignupErrorCode, ClassLinkSignupErrorCode, CleverSignupErrorCode, SchoologySignupErrorCode } from '../../../components/signup/SignupMessages';

export type SignupResult = 'form' | 'check-email' | 'account-exists' | 'domain-error' | 'create-account' | 'error';

interface SignupRoute {
  result: SignupResult;
  productKey: SignupProductKey | null;
  program: string;
  districtAdminProductKey: DistrictAdminProductKey | null;
  errorCode: SignupErrorCode | null;
  domainErrorCode: SignupDomainErrorCode | null;
  accountStatus: 'archived' | null;
  verificationHash: string;
  handoffId: string;
  classLinkState: string;
  classLinkErrorCode: ClassLinkSignupErrorCode | null;
  cleverState: string;
  cleverErrorCode: CleverSignupErrorCode | null;
  canvasState: string;
  canvasErrorCode: CanvasSignupErrorCode | null;
  schoologyState: string;
  schoologyErrorCode: SchoologySignupErrorCode | null;
  subscriptionToken: string;
  searchWithoutToken: string;
}

const cleverSignupErrorCodes: CleverSignupErrorCode[] = [
  'clever_cancelled',
  'clever_authentication_failed',
  'clever_student_not_allowed',
  'clever_campus_mismatch',
  'clever_continuation_invalid',
  'clever_continuation_expired',
  'clever_subscription_unavailable',
];

const classLinkSignupErrorCodes: ClassLinkSignupErrorCode[] = [
  'classlink_cancelled',
  'classlink_authentication_failed',
  'classlink_student_not_allowed',
  'classlink_campus_mismatch',
  'classlink_continuation_invalid',
  'classlink_continuation_expired',
  'classlink_mapping_unavailable',
  'classlink_subscription_unavailable',
];

const schoologySignupErrorCodes: SchoologySignupErrorCode[] = [
  'schoology_authentication_failed',
  'schoology_student_not_allowed',
  'schoology_continuation_invalid',
  'schoology_continuation_expired',
];

const canvasSignupErrorCodes: CanvasSignupErrorCode[] = [
  'canvas_authentication_failed',
  'canvas_student_not_allowed',
  'canvas_continuation_invalid',
  'canvas_continuation_expired',
];

export function getSignupRoute(pathname: string, search: string): SignupRoute {
  const path = pathname.replace(/\/$/, '');
  const searchParams = new URLSearchParams(search);
  let result: SignupResult = 'form';
  if (path.endsWith('/signup/check-email')) {
    result = 'check-email';
  } else if (path.endsWith('/signup/account-exists')) {
    result = 'account-exists';
  } else if (path.endsWith('/signup/domain-error')) {
    result = 'domain-error';
  } else if (path.endsWith('/signup/create-account')) {
    result = 'create-account';
  } else if (path.endsWith('/signup/error')) {
    result = 'error';
  }

  const plan = searchParams.get('plan');
  const productKey = getSignupProductByPlan(plan)?.productKey || null;
  const errorCode = searchParams.get('signup_error_code');
  const accountStatus = searchParams.get('signup_account_status');
  const subscriptionToken = searchParams.get('token')?.trim() || '';
  const allowsProviders = result === 'form' && !productKey;
  const classLinkState = allowsProviders && searchParams.get('provider') === 'classlink'
    ? searchParams.get('classlink_state')?.trim() || ''
    : '';
  const cleverState = allowsProviders && searchParams.get('provider') === 'clever'
    ? searchParams.get('clever_state')?.trim() || ''
    : '';
  const canvasState = allowsProviders && searchParams.get('provider') === 'canvas'
    ? searchParams.get('canvas_state')?.trim() || ''
    : '';
  const schoologyState = allowsProviders && searchParams.get('provider') === 'schoology'
    ? searchParams.get('schoology_state')?.trim() || ''
    : '';
  const requestedClassLinkErrorCode = allowsProviders && searchParams.get('signup_provider') === 'classlink'
    ? searchParams.get('signup_error_code')
    : null;
  const classLinkErrorCode = classLinkSignupErrorCodes.includes(requestedClassLinkErrorCode as ClassLinkSignupErrorCode)
    ? requestedClassLinkErrorCode as ClassLinkSignupErrorCode
    : null;
  const requestedCleverErrorCode = allowsProviders && searchParams.get('signup_provider') === 'clever'
    ? searchParams.get('signup_error_code')
    : null;
  const cleverErrorCode = cleverSignupErrorCodes.includes(requestedCleverErrorCode as CleverSignupErrorCode)
    ? requestedCleverErrorCode as CleverSignupErrorCode
    : null;
  const requestedCanvasErrorCode = allowsProviders && searchParams.get('signup_provider') === 'canvas'
    ? searchParams.get('signup_error_code')
    : null;
  const canvasErrorCode = canvasSignupErrorCodes.includes(requestedCanvasErrorCode as CanvasSignupErrorCode)
    ? requestedCanvasErrorCode as CanvasSignupErrorCode
    : null;
  const requestedSchoologyErrorCode = allowsProviders && searchParams.get('signup_provider') === 'schoology'
    ? searchParams.get('signup_error_code')
    : null;
  const schoologyErrorCode = schoologySignupErrorCodes.includes(requestedSchoologyErrorCode as SchoologySignupErrorCode)
    ? requestedSchoologyErrorCode as SchoologySignupErrorCode
    : null;
  searchParams.delete('token');

  return {
    result,
    productKey,
    program: getProgramBySignupPlan(plan)?.value || '',
    districtAdminProductKey: result === 'form' && searchParams.get('view') === 'district-admin' && isDistrictAdminProductKey(productKey) ? productKey : null,
    errorCode: result === 'error' && (errorCode === 'user_archived' || errorCode === 'verification_link_expired') ? errorCode : null,
    domainErrorCode: result === 'domain-error' && errorCode === 'homeschool_email_mismatch' ? errorCode : null,
    accountStatus: result === 'account-exists' && accountStatus === 'archived' ? accountStatus : null,
    verificationHash: searchParams.get('myhash') || '',
    handoffId: result === 'domain-error' || result === 'account-exists' ? searchParams.get('signup_handoff_id') || '' : '',
    classLinkState,
    classLinkErrorCode,
    cleverState,
    cleverErrorCode,
    canvasState,
    canvasErrorCode,
    schoologyState,
    schoologyErrorCode,
    subscriptionToken,
    searchWithoutToken: searchParams.toString(),
  };
}
